/**
 * FullDocumentDialog — modal that shows the entire indexed text of a
 * document, so users can quickly inspect what the system captured for a
 * search result without having to open the source PDF.
 *
 * Backed by ``GET /api/documents/:id``. Loads lazily when ``open`` becomes
 * true; closing the dialog cancels any in-flight fetch.
 */
import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import type { ReactElement } from "react";

const API_BASE: string = ((): string => {
  const meta = import.meta as { env?: { VITE_API_BASE_URL?: string } };
  return meta.env?.VITE_API_BASE_URL ?? "/api";
})();

export interface FullDocumentDialogProps {
  open: boolean;
  documentId: string | null;
  documentTitle: string | null;
  onClose: () => void;
}

interface FetchedDocument {
  documentId: string;
  title: string | null;
  jurisdictionName: string | null;
  text: string;
  chunkCount: number;
  truncated: boolean;
}

type LoadState =
  | { state: "idle" }
  | { state: "loading" }
  | { state: "ready"; data: FetchedDocument }
  | { state: "error"; message: string };

export function FullDocumentDialog({
  open,
  documentId,
  documentTitle,
  onClose,
}: FullDocumentDialogProps): ReactElement {
  const [load, setLoad] = useState<LoadState>({ state: "idle" });

  useEffect(() => {
    if (!open || !documentId) {
      setLoad({ state: "idle" });
      return;
    }
    const controller = new AbortController();
    setLoad({ state: "loading" });
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/documents/${documentId}`, {
          credentials: "same-origin",
          signal: controller.signal,
        });
        if (!res.ok) {
          setLoad({ state: "error", message: `Could not load document (${res.status}).` });
          return;
        }
        const json = (await res.json()) as FetchedDocument;
        if (!controller.signal.aborted) {
          setLoad({ state: "ready", data: json });
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        const message = error instanceof Error ? error.message : "Network error.";
        setLoad({ state: "error", message });
      }
    })();
    return () => {
      controller.abort();
    };
  }, [open, documentId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: {
          sx: { minHeight: { sm: "80vh" } },
          "aria-label": "Full document content",
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography component="span" variant="h6" noWrap sx={{ pr: 2 }}>
          {documentTitle ?? "Indexed content"}
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {load.state === "loading" ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}
            role="status"
            aria-label="Loading full content"
          >
            <CircularProgress />
          </Box>
        ) : null}
        {load.state === "error" ? <Alert severity="error">{load.message}</Alert> : null}
        {load.state === "ready" ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {load.data.chunkCount} indexed {load.data.chunkCount === 1 ? "chunk" : "chunks"}
              {load.data.truncated ? " — truncated for display" : ""}
            </Typography>
            <Typography
              component="pre"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "inherit",
                m: 0,
              }}
            >
              {load.data.text}
            </Typography>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
